// Menaje y retorno de un despacho: registra los contenedores retornables
// ENVIADOS por parada (al cargar) y, al regreso, los DEVUELTOS + los sobrantes de
// comida, cerrando el despacho. Se embebe en la pantalla de Despacho.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Plus, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering, SERVICIO_LABEL } from './shared';

interface MenajeCat { id: string; nombre: string; valor_unitario: number; es_activo: boolean }
interface MenajeRow { id: string; menaje_id: string; nombre: string; enviada: number; devuelta: number; saldo: number }
interface ParadaMenaje { contrato_id: string; cliente: string; menaje: MenajeRow[] }
interface LineaSrv { id: string; servicio: string; porciones: number }
interface ParadaDet { contrato_id: string; cliente: string; lineas: LineaSrv[] }

export function MenajeRetorno({ despachoId, estado, paradas, onCerrado }: {
  despachoId: string; estado: string; paradas: ParadaDet[]; onCerrado: () => void;
}) {
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState<Record<string, string>>({});       // contrato_id -> menaje_id a agregar
  const [enviados, setEnviados] = useState<Record<string, string>>({}); // key dm.id|nuevo -> cantidad
  const [devueltos, setDevueltos] = useState<Record<string, string>>({});
  const [sobrantes, setSobrantes] = useState<Record<string, string>>({});
  const enRuta = estado === 'en_ruta' || estado === 'cargado';

  const { data: catalogo = [] } = useQuery<MenajeCat[]>({ queryKey: ['cat-menaje-cat'], queryFn: () => catRpc<MenajeCat[]>('cat_menaje_listar') });
  const { data: menaje = [] } = useQuery<ParadaMenaje[]>({
    queryKey: ['cat-desp-menaje', despachoId], queryFn: () => catRpc<ParadaMenaje[]>('cat_despacho_menaje_get', { _despacho_id: despachoId }),
  });
  const catActivo = useMemo(() => catalogo.filter((c) => c.es_activo), [catalogo]);

  const guardarEnviado = useMutation({
    mutationFn: (lineas: { contrato_id: string; menaje_id: string; cantidad_enviada: number }[]) =>
      catRpc('cat_despacho_menaje_set', { _despacho_id: despachoId, _lineas: lineas }),
    onSuccess: () => { toast.success('Menaje enviado registrado'); setNuevo({}); setEnviados({}); void qc.invalidateQueries({ queryKey: ['cat-desp-menaje', despachoId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const retornar = useMutation({
    mutationFn: () => catRpc('cat_despacho_retornar', {
      _despacho_id: despachoId,
      _menaje: menaje.flatMap((p) => p.menaje).filter((m) => devueltos[m.id] !== undefined && devueltos[m.id] !== '')
        .map((m) => ({ id: m.id, cantidad_devuelta: parseInt(devueltos[m.id], 10) })),
      _sobrantes: Object.entries(sobrantes).filter(([, v]) => v !== '')
        .map(([linea_id, v]) => ({ linea_id, porciones_devueltas: parseFloat(v) })),
    }),
    onSuccess: () => { toast.success('Retorno registrado · despacho cerrado'); invalidateCatering(qc); onCerrado(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!enRuta && estado !== 'cerrado') return null;

  const menajeDe = (cid: string) => menaje.find((p) => p.contrato_id === cid)?.menaje ?? [];

  return (
    <div className="space-y-3 border-t pt-3">
      <p className="flex items-center gap-2 text-sm font-medium"><Boxes className="h-4 w-4" /> Menaje retornable y sobrantes</p>
      {!catActivo.length && <p className="text-xs text-amber-600">No hay tipos de menaje. Créalos en Catering → Logística → Menaje y sobrantes → Catálogo.</p>}

      {paradas.map((par) => {
        const rows = menajeDe(par.contrato_id);
        return (
          <div key={par.contrato_id} className="rounded-md border p-2">
            <p className="text-sm font-medium">{par.cliente}</p>
            {/* menaje enviado / devuelto */}
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Contenedor</th><th className="py-1 pr-2 text-right">Enviado</th>
                {estado !== 'cerrado' && <th className="py-1 pr-2 text-right">Devuelto</th>}
                <th className="py-1 pr-2 text-right">Saldo</th>
              </tr></thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="py-1 pr-2">{m.nombre}</td>
                    <td className="py-1 pr-2 text-right font-mono">{m.enviada}</td>
                    {estado !== 'cerrado' && (
                      <td className="py-1 pr-2 text-right">
                        <input className={`${fieldCls} w-20 text-right`} type="number" placeholder={String(m.enviada)}
                          value={devueltos[m.id] ?? (m.devuelta || '')} onChange={(e) => setDevueltos((s) => ({ ...s, [m.id]: e.target.value }))} />
                      </td>
                    )}
                    <td className={`py-1 pr-2 text-right font-mono ${m.saldo > 0 ? 'text-amber-600' : ''}`}>{m.saldo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* agregar tipo enviado (solo antes de cerrar) */}
            {estado !== 'cerrado' && !!catActivo.length && (
              <div className="mt-1 flex items-center gap-2">
                <select className={`${fieldCls} w-40`} value={nuevo[par.contrato_id] ?? ''} onChange={(e) => setNuevo((s) => ({ ...s, [par.contrato_id]: e.target.value }))}>
                  <option value="">+ contenedor…</option>
                  {catActivo.filter((c) => !rows.some((r) => r.menaje_id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
                <input className={`${fieldCls} w-24`} type="number" placeholder="cant. enviada"
                  value={enviados[par.contrato_id] ?? ''} onChange={(e) => setEnviados((s) => ({ ...s, [par.contrato_id]: e.target.value }))} />
                <Button size="sm" variant="outline" disabled={!nuevo[par.contrato_id] || !enviados[par.contrato_id] || guardarEnviado.isPending}
                  onClick={() => guardarEnviado.mutate([{ contrato_id: par.contrato_id, menaje_id: nuevo[par.contrato_id], cantidad_enviada: parseInt(enviados[par.contrato_id], 10) }])}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            {/* sobrantes por servicio (al retornar) */}
            {enRuta && (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <span className="text-xs text-muted-foreground">Sobrantes:</span>
                {par.lineas.map((l) => (
                  <div key={l.id}>
                    <label className="block text-[11px] text-muted-foreground">{SERVICIO_LABEL[l.servicio] ?? l.servicio} (de {num(l.porciones, 0)})</label>
                    <input className={`${fieldCls} w-20`} type="number" placeholder="0" value={sobrantes[l.id] ?? ''} onChange={(e) => setSobrantes((s) => ({ ...s, [l.id]: e.target.value }))} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {enRuta && (
        <Button disabled={retornar.isPending} onClick={() => retornar.mutate()}>
          <PackageOpen className="mr-1 h-4 w-4" /> Registrar retorno y cerrar despacho
        </Button>
      )}
    </div>
  );
}
